import {
  CalendarOutlined,
  CheckOutlined,
  CloseOutlined,
  LinkOutlined,
  PaperClipOutlined,
  PlusOutlined,
  WarningFilled
} from "@ant-design/icons"
import { App, Button, DatePicker, Flex, Form, Input, Tooltip } from "antd"
import dayjs from "dayjs"
import React, { useMemo, useState } from "react"

import { rowCommitKeyDown } from "../../../../../../../components/EntityTable/editable-row-cells.ts"
import { MODAL_CONFIRM_CONFIG } from "../../../../../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import { FormChangedWatcher } from "../../../../../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import { empty } from "../../../../../../../constants/placeholders"
import { useDateFormat } from "../../../../../../../hooks/use-date-format.ts"
import { DATE_FORMAT } from "../../../../../../../utils/date/date.ts"
import { dateAutoFill } from "../../../../../../../utils/form/date-auto-fill.tsx"
import { validateFormWithScroll } from "../../../../../../../utils/form/validate-form-with-scroll.tsx"
import { isFormChanged } from "../../../../../../../utils/object/is-form-changed.ts"
import type { ProjectMilestoneFilterOptions } from "../../../../../redux/project.types.ts"
import type { ProjectMilestone } from "../../../../../types/project-phases.types.tsx"
import { MILESTONE_STATUSES } from "../../../../constants/milestone-statuses.ts"
import type { EffectiveScheduleEntry } from "../../../../utils/milestone-effective-schedule.ts"
import { ResolvedSelect } from "../../../../../../../components/Select/ResolvedSelect.tsx"
import { PlannedMoneyInput } from "../../../../../components/PlannedMoneyInput.tsx"
import { ORDER_COLUMN_KEY } from "./columns.tsx"
import { MilestoneRiskTag } from "./MilestoneRiskTag.tsx"

interface ProjectViewMilestoneNewRowProps {
  record: ProjectMilestone
  children?: React.ReactNode[]
  options: ProjectMilestoneFilterOptions
  mode?: "create" | "edit"
  /** When this milestone's dates are currently shifted by a dependency, the
   * edit form must open on those same calculated dates — not the raw stored
   * ones the read-only row no longer shows — or the two views visibly
   * disagree the moment you open the row. */
  effectiveEntry?: EffectiveScheduleEntry
  onSelectRow: (
    record: ProjectMilestone,
    action: string
  ) => Promise<void> | void
}

export const ProjectViewMilestoneNewRow = ({
  record,
  onSelectRow,
  options,
  mode = "create",
  effectiveEntry,
  ...rest
}: ProjectViewMilestoneNewRowProps) => {
  // Everything the table puts in front of the editors — selection, reorder
  // grip, expand chevron, # — ends at the cell carrying the `#` column's key.
  const leadingCells = useMemo(() => {
    const cells: React.ReactNode[] = []
    React.Children.forEach(rest?.children, (child) => cells.push(child))
    const orderAt = cells.findIndex(
      (cell) =>
        React.isValidElement(cell) && String(cell.key) === ORDER_COLUMN_KEY
    )
    // Without a # cell there is no telling the table's cells from this row's
    // own, and passing all of them through would render the row twice over.
    return orderAt < 0 ? [] : cells.slice(0, orderAt + 1)
  }, [rest?.children])

  const { getDateValueProps, createDateRangeRules, normalizeDate, userFormat } =
    useDateFormat()
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<ProjectMilestone>()
  const { modal } = App.useApp()

  const initialValues = useMemo(
    () =>
      effectiveEntry?.isShifted
        ? {
            ...record,
            start_date: effectiveEntry.start.format(userFormat || DATE_FORMAT),
            end_date: effectiveEntry.end.format(userFormat || DATE_FORMAT)
          }
        : record,
    [record, effectiveEntry, userFormat]
  )

  const watch = Form.useWatch(
    (s) => ({
      status: s?.status,
      actualEnd: s?.actual_end_date,
      plannedEnd: s?.end_date
    }),
    form
  )

  const isCompleted = watch?.status === MILESTONE_STATUSES.COMPLETED
  const completedWithoutActualEnd = isCompleted && !watch?.actualEnd
  const isLate =
    watch?.actualEnd &&
    watch?.plannedEnd &&
    dayjs(watch.actualEnd).isAfter(dayjs(watch.plannedEnd))

  const handleSave = async () => {
    try {
      const { id, ...initialData } = record
      const values = await validateFormWithScroll(form)

      setSaving(true)

      const action = mode === "create" ? "create" : "update"
      await onSelectRow(
        {
          // ...initialData,
          ...values,
          start_date: normalizeDate(values?.start_date),
          end_date: normalizeDate(values?.end_date),
          actual_end_date: normalizeDate(values?.actual_end_date)
        },
        action
      )
    } catch (error: any) {
      if (!error?.errorFields) throw error
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    const action = mode === "create" ? "cancel_create" : "cancel"

    const isChanged = isFormChanged<ProjectMilestone>(
      form.getFieldsValue(),
      initialValues
    )

    if (isChanged) {
      modal.confirm({
        ...MODAL_CONFIRM_CONFIG.cancelChanges,
        onOk() {
          onSelectRow(record, action)
        }
      })
    } else {
      onSelectRow(record, action)
    }
  }

  const onStatusChangeHandler = (value: number) => {
    if (value === MILESTONE_STATUSES.COMPLETED) {
      if (!form.getFieldValue("actual_end_date")) {
        form.setFieldValue("actual_end_date", dayjs().format(DATE_FORMAT))
      }
    }
  }

  // Symmetric to the above: entering when a milestone actually finished
  // normally means it's done, so default the status forward too — the same
  // existing status behavior, not a second one. Only on entering a value
  // (never on clearing it) and only when status isn't already Completed, so
  // this never fights a status the user set on purpose.
  const onActualEndChangeHandler = (value: dayjs.Dayjs | null) => {
    if (value && form.getFieldValue("status") !== MILESTONE_STATUSES.COMPLETED) {
      form.setFieldValue("status", MILESTONE_STATUSES.COMPLETED)
    }
  }

  const rowClassName = mode === "create" ? "bg-blue-50" : ""

  const onKeyDownRow = rowCommitKeyDown(() => void handleSave(), {
    busy: saving
  })

  return (
    <Form
      form={form}
      component={false}
      initialValues={initialValues}
      disabled={saving}
      validateTrigger={["onSubmit", "onChange"]}
    >
      <FormChangedWatcher<ProjectMilestone> form={form} saving={saving} />

      <tr {...rest} onKeyDown={onKeyDownRow} className={rowClassName}>
        {/* Counting these instead goes wrong the moment the table gains or
            loses a leading column — that is how every field from Milestone Name
            on ended up a column to the left. Wrapping them would nest a <td>
            inside a <td>, out of reach of any stylesheet. */}
        {leadingCells}

        {/* Milestone Name */}
        <td className="ant-table-cell">
          <Form.Item<ProjectMilestone>
            name="name"
            noStyle
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Milestone name is required"
              },
              {
                max: 255,
                message: "Must be at most 255 characters"
              }
            ]}
          >
            <Input.TextArea autoSize placeholder="Enter name" />
          </Form.Item>
        </td>

        {/* Status */}
        <td className="ant-table-cell">
          <Form.Item<ProjectMilestone> name="milestone_category" noStyle>
            <ResolvedSelect
              className="w-full"
              placeholder="Enter type"
              options={options?.["category"]}
            />
          </Form.Item>
        </td>

        {/* Status */}
        <Tooltip
          title={
            completedWithoutActualEnd
              ? "Milestone should be completed with actual end date"
              : ""
          }
          color="orange"
        >
          <td className="ant-table-cell">
            <Form.Item<ProjectMilestone>
              name="status"
              noStyle
              rules={[{ required: true, message: "Required" }]}
            >
              <ResolvedSelect
                status={completedWithoutActualEnd ? "warning" : ""}
                // Fills the cell rather than setting its own width, so the
                // column's declared width is what decides it.
                className="w-full"
                placeholder="Enter status"
                onChange={onStatusChangeHandler}
                options={options?.["status"]}
              />
            </Form.Item>
          </td>
        </Tooltip>

        {/* Risk */}
        <td className="ant-table-cell text-center">
          <MilestoneRiskTag risk={record?.schedule_risk} />
        </td>

        {/* Comments */}
        <td className="ant-table-cell">
          <Form.Item<ProjectMilestone>
            name="description"
            noStyle
            rules={[
              {
                whitespace: true,
                max: 250,
                message: "Must be at least 250 characters"
              }
            ]}
          >
            <Input.TextArea rows={2} autoSize placeholder="Enter description" />
          </Form.Item>
        </td>

        {/* Receivables */}
        <td className="ant-table-cell text-center">
          <Tooltip title="Planned receivable">
            <PlannedMoneyInput name="planned_receivable" className="m-0" />
          </Tooltip>
        </td>

        {/* Payables */}
        <td className="ant-table-cell text-center">
          <Tooltip title="Planned payable. Enter the payable lines by hand; the row flags Committed when it differs from this figure.">
            <PlannedMoneyInput name="planned_payable" className="m-0" />
          </Tooltip>
        </td>

        {/* Planned Start */}
        <td className="ant-table-cell">
          <Form.Item<ProjectMilestone>
            name="start_date"
            noStyle
            getValueProps={getDateValueProps}
            dependencies={["end_date"]}
            rules={createDateRangeRules<ProjectMilestone>("end_date", "start")}
          >
            <DatePicker
              onChange={dateAutoFill<ProjectMilestone>({
                form,
                endDatePath: ["end_date"]
                // additionalEndDatePaths: ["actual_end_date"]
              })}
            />
          </Form.Item>
        </td>

        {/* Planned End */}
        <td className="ant-table-cell">
          <Form.Item<ProjectMilestone>
            name="end_date"
            noStyle
            getValueProps={getDateValueProps}
            dependencies={["start_date"]}
            rules={createDateRangeRules<ProjectMilestone>("start_date", "end")}
          >
            <DatePicker />
          </Form.Item>
        </td>

        {/* Actual End */}
        <Tooltip
          title={
            completedWithoutActualEnd
              ? "Milestone should be completed with actual end date"
              : ""
          }
          color="orange"
        >
          <td className="ant-table-cell">
            <Form.Item<ProjectMilestone>
              name="actual_end_date"
              noStyle
              getValueProps={getDateValueProps}
            >
              <DatePicker
                status={completedWithoutActualEnd ? "warning" : ""}
                onChange={onActualEndChangeHandler}
                suffixIcon={
                  isLate ? (
                    <WarningFilled className="text-warning" />
                  ) : (
                    <CalendarOutlined />
                  )
                }
              />
            </Form.Item>
          </td>
        </Tooltip>

        {/* Variance */}
        <td className="ant-table-cell text-center">
          {record?.variance || empty}
        </td>

        {/* Dependencies */}
        <td className="ant-table-cell text-center">
          <Tooltip title="Only available in view mode">
            <Button disabled icon={<LinkOutlined />} />
          </Tooltip>
        </td>

        {/* Evidence */}
        <td className="ant-table-cell text-center">
          <Tooltip title="Only available in view mode">
            <Button disabled icon={<PaperClipOutlined />} />
          </Tooltip>
        </td>

        {/* Actions */}
        <td
          className={`sticky right-0 ant-table-cell ant-table-cell-fix-right ant-table-cell-fix-right-first ${rowClassName}`}
        >
          <Flex gap="small" justify="center">
            <Button
              variant="outlined"
              color="danger"
              onClick={handleCancel}
              icon={<CloseOutlined />}
            />
            <Button
              loading={saving}
              variant="outlined"
              color="green"
              onClick={handleSave}
              icon={mode === "create" ? <PlusOutlined /> : <CheckOutlined />}
            />
          </Flex>
        </td>
      </tr>
    </Form>
  )
}
